import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Clock3, CreditCard, Info, KeyRound, Wallet } from "lucide-react";
import { toast } from "sonner";
import { adminPlatformSettingService } from "@/services/admin-platform-setting.service";
import { type PlatformSettingListDto } from "@/types/platform-setting";
import {
  stripeSettingsSchema,
  type StripeSettingsFormData,
} from "@/schemas/stripe-settings";
import {
  SettingsField,
  SettingsFormFooter,
  SettingsFormSkeleton,
  SettingsLoadError,
  SettingsSection,
  SettingsSwitch,
} from "./SettingsForm";

const TYPE_TO_FIELD: Record<string, keyof StripeSettingsFormData> = {
  StripeEnabled: "stripeEnabled",
  StripePublishableKey: "stripePublishableKey",
};

/**
 * Stripe counts as on only for a literal "true" — the same test `StripeFeatureService` applies —
 * so a missing or malformed row reads as off here too, rather than showing a switch that is on
 * while customers never see Stripe.
 */
function isEnabledValue(value: string | undefined): boolean {
  return value?.toLowerCase() === "true";
}

function settingsToForm(
  settings: Record<string, PlatformSettingListDto>,
): StripeSettingsFormData {
  return {
    stripeEnabled: isEnabledValue(settings.StripeEnabled?.value),
    stripePublishableKey: settings.StripePublishableKey?.value ?? "",
  };
}

export function StripeSettingsTab() {
  const queryClient = useQueryClient();

  const {
    data: stripeSettings,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["admin", "platform-settings", "stripe"],
    queryFn: () => adminPlatformSettingService.getStripeSettings(),
  });

  const form = useForm<StripeSettingsFormData>({
    resolver: zodResolver(stripeSettingsSchema),
    defaultValues: {
      stripeEnabled: false,
      stripePublishableKey: "",
    },
  });

  useEffect(() => {
    if (stripeSettings) {
      form.reset(settingsToForm(stripeSettings));
    }
  }, [stripeSettings, form]);

  const updateMutation = useMutation({
    mutationFn: async (data: StripeSettingsFormData) => {
      const changes: { id: string; value: string }[] = [];
      const missing: string[] = [];

      for (const [type, field] of Object.entries(TYPE_TO_FIELD)) {
        const setting = stripeSettings?.[type];
        const newValue = String(data[field]);

        if (!setting) {
          // This screen only updates rows, so an edit to a field whose row does not exist would
          // otherwise be dropped while the form kept showing it as unsaved.
          if (form.getFieldState(field).isDirty) missing.push(type);
          continue;
        }

        if (setting.value !== newValue) {
          changes.push({ id: setting.id, value: newValue });
        }
      }

      // Checked before any request goes out, so a refused save never lands half of the form.
      if (missing.length > 0) {
        throw new Error(
          `No stored setting for ${missing.join(", ")}. Add it in the General tab first.`,
        );
      }

      if (changes.length === 0) {
        return 0;
      }

      const results = await Promise.allSettled(
        changes.map((change) =>
          adminPlatformSettingService.updateSetting(change.id, {
            value: change.value,
          }),
        ),
      );
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0) {
        throw new Error(`${failed.length} setting(s) failed to update`);
      }

      return changes.length;
    },
    onSuccess: (count) => {
      if (count === 0) {
        toast.info("No changes to save");
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["admin", "platform-settings"],
      });
      toast.success(`${count} Stripe setting(s) updated`);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const { isDirty } = form.formState;
  const stripeEnabled = form.watch("stripeEnabled");
  // The badge describes what is stored, not the switch: an unsaved toggle has changed nothing yet.
  // StripeFeatureService ignores an inactive row, so one reads as disabled here as well.
  const savedEnabled =
    !!stripeSettings?.StripeEnabled?.isActive &&
    isEnabledValue(stripeSettings.StripeEnabled.value);

  return (
    <div className="grid items-start gap-5 2xl:grid-cols-[minmax(0,1fr)_360px]">
      <Card className="overflow-hidden shadow-sm">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 border-b bg-muted/30 p-5">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <CreditCard className="size-4" />
              </span>
              Stripe Configuration
            </CardTitle>
            <CardDescription className="max-w-2xl leading-6">
              Card payments on the customer deposit page.
            </CardDescription>
          </div>
          {!isLoading && !isError && (
            <span
              className={
                savedEnabled
                  ? "inline-flex shrink-0 items-center gap-1.5 rounded-full bg-ok-soft px-2.5 py-1 text-xs font-medium text-ok-ink"
                  : "inline-flex shrink-0 items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground"
              }
            >
              <span
                className={`size-1.5 rounded-full ${savedEnabled ? "bg-ok" : "bg-muted-foreground/50"}`}
              />
              {savedEnabled ? "Enabled" : "Disabled"}
            </span>
          )}
        </CardHeader>

        {isLoading ? (
          <SettingsFormSkeleton rows={2} />
        ) : isError ? (
          <SettingsLoadError onRetry={() => void refetch()} />
        ) : (
          <form
            onSubmit={form.handleSubmit(
              (data) => updateMutation.mutate(data),
              (errors) => {
                const firstError = Object.values(errors)[0];
                if (firstError?.message) {
                  toast.error(firstError.message);
                }
              },
            )}
          >
            <CardContent className="divide-y p-0">
              <SettingsSection
                title="Availability"
                description="Whether customers can pay by card."
              >
                <SettingsSwitch
                  id="stripeEnabled"
                  label="Enable Stripe payments"
                  description={
                    stripeSettings?.StripeEnabled &&
                    !stripeSettings.StripeEnabled.isActive
                      ? "This setting is marked inactive, so Stripe stays off whatever the switch says. Activate it in the General tab."
                      : stripeEnabled
                        ? "Customers can top up their balance by card."
                        : "The card option is hidden from customers."
                  }
                  checked={stripeEnabled}
                  onCheckedChange={(checked) =>
                    form.setValue("stripeEnabled", checked, {
                      shouldDirty: true,
                    })
                  }
                />
              </SettingsSection>

              <SettingsSection
                title="API key"
                description="The public half of the Stripe key pair."
              >
                {/* StripeFeatureService falls back to the deployment's Stripe:PublishableKey
                    when this row is empty, so an empty field is not a broken setup. */}
                <SettingsField
                  id="stripePublishableKey"
                  label="Publishable key"
                  error={form.formState.errors.stripePublishableKey?.message}
                  hint="Safe to expose in the browser. Leave empty to use the key from the deployment configuration."
                >
                  <Input
                    id="stripePublishableKey"
                    placeholder="pk_live_..."
                    autoComplete="off"
                    spellCheck={false}
                    className="font-mono"
                    {...form.register("stripePublishableKey")}
                  />
                </SettingsField>
              </SettingsSection>
            </CardContent>

            <SettingsFormFooter
              isDirty={isDirty}
              isSaving={updateMutation.isPending}
              onDiscard={() =>
                form.reset(
                  stripeSettings ? settingsToForm(stripeSettings) : undefined,
                )
              }
            />
          </form>
        )}
      </Card>

      {/* Each point is what StripeFeatureService actually does — the reasons a switch that is on
          can still leave customers without the card option. */}
      <Card className="shadow-sm">
        <CardHeader className="p-5 pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Info className="size-4 text-primary" /> How it goes live
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-0">
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li className="flex gap-2.5">
              <KeyRound className="mt-0.5 size-4 shrink-0" />
              <span>
                The secret key is not set here. It lives in the deployment
                configuration, and without it Stripe stays off even when
                enabled.
              </span>
            </li>
            <li className="flex gap-2.5">
              <Wallet className="mt-0.5 size-4 shrink-0" />
              <span>
                Customers see the card option on the Deposit page once both
                are in place.
              </span>
            </li>
            <li className="flex gap-2.5">
              <Clock3 className="mt-0.5 size-4 shrink-0" />
              {/* 60s server cache, plus the client's 5-minute staleTime on useFeatureFlags. */}
              <span>
                Saved changes reach customers within a few minutes; an open
                console picks them up on its next refresh.
              </span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
